import { Check, HelpCircle, Info, Search, X } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  cola as colaDeCuenta,
  colorDeBanco,
  type Candidatos as Datos,
  dinero,
  fecha,
  RAYA,
  type Voucher,
} from "./comun";

// ===========================================================================
// LOS MOVIMIENTOS QUE PODRÍAN SER ESTE PAGO.
//
// Tres decisiones que parecen de detalle y no lo son:
//
// 1. EL CRITERIO SE VE Y SE TOCA. Una lista filtrada que no dice por qué está
//    filtrada es la forma más fácil de que alguien crea que faltan datos. La
//    barra de arriba enseña la cuenta, la ventana de días y la tolerancia de
//    importe, y las tres se cambian sin salir de la pantalla.
//
// 2. EL NÚMERO DE OPERACIÓN NO FILTRA. Cada banco numera a su manera y el del
//    voucher casi nunca es el del extracto: filtrar por él vaciaría la tabla
//    justo cuando el pago SÍ está. Se enseña al lado del que dice el voucher
//    para que lo compare una persona, que es quien sabe leer «difieren en el
//    último dígito».
//
// 3. NADA SE ATA SOLO. La fila con más evidencia sale la primera y marcada,
//    pero hay que elegirla y firmarla. Es la misma compuerta que la revisión
//    guiada, y por el mismo motivo: si se pudiera aplicar de un clic, revisar
//    se convertiría en aceptar lo que propone la máquina.
// ===========================================================================

export type Criterios = {
  diasAntes: number;
  diasDespues: number;
  tolerancia: number;
  todasLasCuentas: boolean;
  ocultarConciliados: boolean;
  buscar: string;
};

const DIAS_ANTES = [0, 1, 3, 7];
const DIAS_DESPUES = [0, 2, 3, 5, 7, 15];
/** En céntimos: exacto, cinco soles, ocho, veinte. */
const TOLERANCIAS = [0, 500, 800, 2000];

export function TablaDeCandidatos({
  datos,
  cargando,
  voucher,
  elegido,
  alElegir,
  criterios,
  alCambiar,
}: {
  datos: Datos | null;
  cargando: boolean;
  voucher: Voucher;
  elegido: string | null;
  alElegir: (movimientoId: string | null) => void;
  criterios: Criterios;
  alCambiar: (c: Partial<Criterios>) => void;
}) {
  const c = datos?.criterio;

  // `shrink-0` no es decorativo. La zona de trabajo es un flex en columna y
  // esta sección lleva `overflow-hidden`, lo que anula su `min-height:auto` y
  // deja que el flex la comprima: la tabla entera se encogía a dieciséis
  // píxeles y desaparecía debajo de la barra de acciones.
  return (
    <section className="shrink-0 overflow-hidden rounded-lg border bg-card shadow-sm">
      {/* --- con qué criterio se buscó ------------------------------------ */}
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2.5">
        <Campo etiqueta="Cuenta">
          {criterios.todasLasCuentas || !c?.cuenta ? (
            <span className="text-muted-foreground">Todas las del grupo</span>
          ) : (
            <>
              <span
                className="size-2 rounded-[2px]"
                style={{ background: colorDeBanco(c.cuenta) }}
              />
              {c.cuenta}
            </>
          )}
        </Campo>
        <button
          type="button"
          onClick={() =>
            alCambiar({ todasLasCuentas: !criterios.todasLasCuentas })
          }
          className="text-[11.5px] text-cyan-700 underline-offset-2 hover:underline dark:text-cyan-400"
        >
          {criterios.todasLasCuentas
            ? "Volver a la cuenta del voucher"
            : "Buscar en todas"}
        </button>

        <Campo etiqueta="Fecha">{fecha(c?.fecha ?? voucher.fechaPago)}</Campo>
        <Segmentado
          etiqueta="días antes"
          valores={DIAS_ANTES}
          valor={criterios.diasAntes}
          alElegir={(v) => alCambiar({ diasAntes: v })}
        />
        <Segmentado
          etiqueta="días después"
          valores={DIAS_DESPUES}
          valor={criterios.diasDespues}
          alElegir={(v) => alCambiar({ diasDespues: v })}
        />

        <Campo etiqueta="Importe">
          {dinero(c?.importe ?? voucher.importe, voucher.moneda)}
        </Campo>
        <Segmentado
          etiqueta="soles ±"
          valores={TOLERANCIAS}
          valor={criterios.tolerancia}
          alElegir={(v) => alCambiar({ tolerancia: v })}
          comoTexto={(v) => (v / 100).toString()}
        />

        <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <input
            type="checkbox"
            checked={criterios.ocultarConciliados}
            onChange={(e) =>
              alCambiar({ ocultarConciliados: e.target.checked })
            }
            className="size-3.5 accent-cyan-600"
          />
          Ocultar los ya conciliados
        </label>

        <div className="ml-auto flex h-7 min-w-[150px] items-center gap-1.5 rounded-md border bg-background px-2">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            value={criterios.buscar}
            onChange={(e) => alCambiar({ buscar: e.target.value })}
            placeholder="Glosa u operación…"
            className="min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground"
          />
          {criterios.buscar ? (
            <button
              type="button"
              onClick={() => alCambiar({ buscar: "" })}
              aria-label="Limpiar la búsqueda"
            >
              <X className="size-3 text-muted-foreground" />
            </button>
          ) : null}
        </div>
      </div>

      {/* --- lo que salió ------------------------------------------------- */}
      {cargando ? (
        <div className="space-y-2 p-3">
          {["a", "b", "c"].map((k) => (
            <Skeleton key={k} className="h-11" />
          ))}
        </div>
      ) : !datos || datos.filas.length === 0 ? (
        <SinCandidatos
          voucher={voucher}
          criterios={criterios}
          alCambiar={alCambiar}
          universo={c?.universo ?? 0}
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="w-9 py-2 pl-3" />
                  <th className="py-2 pr-3 text-left font-semibold">Fecha</th>
                  <th className="py-2 pr-3 text-left font-semibold">Cuenta</th>
                  <th className="py-2 pr-3 text-left font-semibold">
                    Lo que dice el banco
                  </th>
                  <th className="py-2 pr-3 text-left font-semibold">
                    N.º operación
                  </th>
                  <th className="py-2 pr-3 text-right font-semibold">
                    Importe
                  </th>
                  <th className="py-2 pr-3 text-left font-semibold">
                    Qué coincide
                  </th>
                  <th className="py-2 pr-3 text-left font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {datos.filas.map((f) => {
                  const marcada = f.id === elegido;
                  const libre = f.restante > 0;
                  return (
                    <tr
                      key={f.id}
                      onClick={() => libre && alElegir(marcada ? null : f.id)}
                      className={cn(
                        "border-b last:border-0",
                        marcada && "bg-cyan-500/10",
                        libre
                          ? "cursor-pointer hover:bg-muted/50"
                          : "text-muted-foreground",
                      )}
                    >
                      <td className="py-2.5 pl-3">
                        <span
                          className={cn(
                            "block size-4 rounded-full border-[1.5px]",
                            marcada
                              ? "border-cyan-500 bg-cyan-500 ring-2 ring-cyan-500/25 ring-inset"
                              : libre
                                ? "border-muted-foreground/40"
                                : "border-muted-foreground/30 border-dashed",
                          )}
                        />
                      </td>

                      <td className="whitespace-nowrap py-2.5 pr-3 align-top">
                        <b className="font-semibold tabular-nums">
                          {fecha(f.fecha)}
                        </b>
                        <div
                          className={cn(
                            "text-[11px] tabular-nums",
                            f.dias === 0
                              ? "font-semibold text-emerald-700 dark:text-emerald-400"
                              : (f.dias ?? 0) < 0
                                ? "text-red-700 dark:text-red-400"
                                : "text-muted-foreground",
                          )}
                        >
                          {f.dias === null
                            ? RAYA
                            : f.dias === 0
                              ? "mismo día"
                              : f.dias > 0
                                ? `+${f.dias} ${f.dias === 1 ? "día" : "días"}`
                                : `${-f.dias} antes`}
                        </div>
                      </td>

                      <td className="whitespace-nowrap py-2.5 pr-3 align-top">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="size-2 shrink-0 rounded-[2px]"
                            style={{ background: colorDeBanco(f.banco) }}
                          />
                          {f.banco}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {colaDeCuenta(f.cuenta)}
                        </div>
                      </td>

                      <td className="max-w-[26rem] py-2.5 pr-3 align-top">
                        <div className="truncate" title={f.descripcion}>
                          {f.descripcion}
                        </div>
                        {/* El nombre del pagador dentro de la glosa. Es lo que
                            resuelve la mirada cuando no hay operación, que es
                            lo normal en Yape y Plin. */}
                        {f.telefonoEnLaGlosa ? (
                          <div className="text-[11px] text-emerald-700 dark:text-emerald-400">
                            el teléfono de la ficha: {f.telefonoEnLaGlosa}
                          </div>
                        ) : null}
                        {f.nombreEnLaGlosa ? (
                          <div
                            className={cn(
                              "text-[11px]",
                              f.nombreEnLaGlosa.fuerte
                                ? "text-emerald-700 dark:text-emerald-400"
                                : "text-muted-foreground",
                            )}
                          >
                            {f.nombreEnLaGlosa.de === "boleta"
                              ? "el cliente de la boleta: "
                              : "quien pagó: "}
                            {f.nombreEnLaGlosa.casados.join(" ")}
                          </div>
                        ) : f.detalle ? (
                          <div className="truncate text-[11px] text-muted-foreground">
                            {f.detalle}
                          </div>
                        ) : null}
                      </td>

                      <td className="py-2.5 pr-3 align-top">
                        <span className="font-mono text-[12px]">
                          {f.operacion ?? RAYA}
                        </span>
                        <div className="text-[11px] text-muted-foreground">
                          {textoDeLaOperacion(f.operacionCompara)}
                        </div>
                      </td>

                      <td className="whitespace-nowrap py-2.5 pr-3 text-right align-top">
                        <b className="font-semibold tabular-nums">
                          {dinero(f.importe, f.moneda)}
                        </b>
                        <div
                          className={cn(
                            "text-[11px] tabular-nums",
                            f.diferencia === 0
                              ? "font-semibold text-emerald-700 dark:text-emerald-400"
                              : "text-muted-foreground",
                          )}
                        >
                          {f.diferencia === null
                            ? RAYA
                            : f.diferencia === 0
                              ? "exacto"
                              : `${f.diferencia > 0 ? "+" : ""}${(f.diferencia / 100).toFixed(2)}`}
                        </div>
                      </td>

                      <td className="py-2.5 pr-3 align-top">
                        <div className="flex flex-wrap gap-1">
                          {f.senales.map((s) => (
                            <Senal key={s.clave} veredicto={s.veredicto}>
                              {s.texto}
                            </Senal>
                          ))}
                        </div>
                      </td>

                      <td className="whitespace-nowrap py-2.5 pr-3 align-top">
                        <span
                          className={cn(
                            "text-[11.5px]",
                            f.estadoTono === "ok"
                              ? "text-emerald-700 dark:text-emerald-400"
                              : f.estadoTono === "warn"
                                ? "text-amber-700 dark:text-amber-400"
                                : "text-muted-foreground",
                          )}
                        >
                          {f.estadoEtiqueta}
                        </span>
                        {f.tomadoPor ? (
                          <div className="text-[11px] text-muted-foreground">
                            {f.restante > 0
                              ? `queda ${dinero(f.restante, f.moneda)}`
                              : `ya la tiene ${f.tomadoPor}`}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t bg-muted/40 px-3 py-2 text-[11.5px] text-muted-foreground">
            <Info className="size-3.5 shrink-0" />
            <span>
              El número de operación{" "}
              <b className="text-foreground">no filtra</b>: cada banco numera a
              su manera y el del voucher casi nunca es el del extracto. Se
              enseña para compararlo.
            </span>
            <span className="ml-auto tabular-nums">
              {datos.total} {datos.total === 1 ? "candidato" : "candidatos"}
              {datos.total > datos.filas.length
                ? ` · se enseñan ${datos.filas.length}`
                : ""}{" "}
              · buscados sobre {c?.universo ?? 0} abonos
            </span>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Cuando no sale ninguno.
 *
 * No basta con decir «sin resultados»: lo que hace falta es saber qué mover.
 * Los tres botones son las tres razones por las que un pago no aparece —la
 * ventana corta, el importe que no cuadra al céntimo, o la cuenta equivocada—
 * y están en el orden en que conviene probarlas.
 */
function SinCandidatos({
  voucher,
  criterios,
  alCambiar,
  universo,
}: {
  voucher: Voucher;
  criterios: Criterios;
  alCambiar: (c: Partial<Criterios>) => void;
  universo: number;
}) {
  const sinFecha = !voucher.fechaPago;
  const sinImporte = voucher.importe === null;

  return (
    <div className="px-4 py-8 text-center">
      <p className="font-medium text-[13px]">
        Ningún movimiento encaja con este voucher
      </p>
      <p className="mx-auto mt-1 max-w-[58ch] text-[12px] text-muted-foreground">
        {sinFecha || sinImporte
          ? "El voucher no trae los datos con los que se busca: sin fecha o sin importe no hay por dónde empezar. Se puede corregir la lectura desde la ficha del expediente."
          : `Se buscó sobre ${universo} abonos. O el extracto de esa cuenta todavía no está cargado, o hay que abrir el criterio.`}
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {criterios.diasDespues < 15 ? (
          <Boton onClick={() => alCambiar({ diasDespues: 15 })}>
            Ampliar a 15 días después
          </Boton>
        ) : null}
        {criterios.tolerancia < 2000 ? (
          <Boton onClick={() => alCambiar({ tolerancia: 2000 })}>
            Admitir ± 20 soles
          </Boton>
        ) : null}
        {!criterios.todasLasCuentas ? (
          <Boton onClick={() => alCambiar({ todasLasCuentas: true })}>
            Buscar en todas las cuentas
          </Boton>
        ) : null}
      </div>
    </div>
  );
}

function Boton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-8 items-center rounded-md border px-3 text-[12.5px] transition-colors hover:bg-muted"
    >
      {children}
    </button>
  );
}

function Campo({
  etiqueta,
  children,
}: {
  etiqueta: string;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-md border bg-background px-2 text-[12px]">
      <span className="text-[11px] text-muted-foreground">{etiqueta}</span>
      {children}
    </span>
  );
}

function Segmentado({
  etiqueta,
  valores,
  valor,
  alElegir,
  comoTexto = (v) => String(v),
}: {
  etiqueta: string;
  valores: number[];
  valor: number;
  alElegir: (v: number) => void;
  comoTexto?: (v: number) => string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex h-7 overflow-hidden rounded-md border bg-background">
        {valores.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => alElegir(v)}
            aria-pressed={v === valor}
            className={cn(
              "border-l px-2 text-[12px] tabular-nums transition-colors first:border-l-0",
              v === valor
                ? "bg-cyan-500/15 font-semibold text-cyan-700 dark:text-cyan-400"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            {comoTexto(v)}
          </button>
        ))}
      </span>
      <span className="text-[11px] text-muted-foreground">{etiqueta}</span>
    </span>
  );
}

function Senal({
  veredicto,
  children,
}: {
  veredicto: "coincide" | "difiere" | "no_se_puede";
  children: React.ReactNode;
}) {
  const Icono =
    veredicto === "coincide" ? Check : veredicto === "difiere" ? X : HelpCircle;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10.5px]",
        veredicto === "coincide"
          ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
          : veredicto === "difiere"
            ? "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400"
            : "bg-muted text-muted-foreground",
      )}
    >
      <Icono className="size-2.5" strokeWidth={3} />
      {children}
    </span>
  );
}

function textoDeLaOperacion(
  c: "identico" | "mismo_final" | "difieren" | "no_comparable",
): string {
  if (c === "identico") return "la misma del voucher";
  if (c === "mismo_final") return "acaba igual";
  if (c === "difieren") return "no es la del voucher";
  return "no se puede comparar";
}
